import type MockAdapter from 'axios-mock-adapter'
import { currentUserId } from '../engine/context'
import { findRow, readTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { handle, idOf, roleCodesOf, statusCodeOf } from './c07.shared'
import {
  COMMAND_CENTER_BATCH_ROLES,
  COMMAND_CENTER_CARDS,
  type CommandCenterCardKey,
  type CommandCenterRole
} from './commandCenter.catalog'
import { almCards, type Card, discDeadlineFg, flow14, slaThresholds, type Tone } from './inventoryDashboard'
import { stockFg, stockRm } from './inventoryStock'
import { invScopeOf } from './invScope'
import { suggestions } from './mergeConsolidation.handler'
import { whOf } from './opname.shared'

/**
 * GEN-01 Dashboard Utama (Command Center) — kartu "Untuk Saya" per role persis prototype `command-center.html`
 * (`card()` + `KONTEKS` + `kartuAlm()`), ringkasan stok & dokumen per cakupan, dan aktivitas terakhir. Daftar kartu per
 * role ada di satu katalog (`commandCenter.catalog.ts`). Pembacaan kartu ALM / selisih ikut menjalankan sapuan otomatis
 * (auto-approve ALM, eskalasi selisih) seperti INV-03.
 */

type Cls = 'warn' | 'ok' | 'run'

const OPEN_TASK = (row: LocalRow) => !['COMPLETED', 'CANCELLED'].includes(String(row.task_status))
const hoursSince = (iso: unknown) => (Date.now() - new Date(String(iso ?? '')).getTime()) / 3600_000
const fmt = (value: number) => value.toLocaleString('id-ID')

/** Umur teks prototype `C.umurTeks`: "<1 jam" = menit, "<24 jam" = "3,5j", selebihnya "3h 14j". */
export const ageText = (hours: number) => {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`
  if (hours < 24) return `${String(Math.round(hours * 10) / 10).replace('.', ',')}j`
  return `${Math.floor(hours / 24)}h ${Math.round(hours % 24)}j`
}

/** Prototype `card()`: nada & chip bawaan dari kelas kartu (warn / ok / berjalan) bila tidak ada konteks khusus. */
function card(
  key: string,
  to: string | null,
  n: number,
  label: string,
  cls: Cls = 'run',
  context: Partial<Pick<Card, 'tone' | 'chip' | 'chip_tone' | 'foot'>> = {}
): Card {
  const tone: Tone = cls === 'warn' ? (n ? 'warning' : 'neutral') : cls === 'ok' ? 'success' : n ? 'info' : 'neutral'
  const finalTone = context.tone ?? tone
  const chip =
    finalTone === 'danger'
      ? 'Perlu tindakan'
      : finalTone === 'warning'
        ? 'Perhatian'
        : finalTone === 'success'
          ? 'Aman'
          : finalTone === 'info'
            ? 'Berjalan'
            : 'Kosong'
  return {
    key,
    n,
    tone: finalTone,
    chip: context.chip ?? chip,
    chip_tone: context.chip_tone ?? finalTone,
    label,
    foot: context.foot ?? '',
    to
  }
}

const byStatus = (collection: string, codes: string[]) =>
  readTable(collection).filter(row => codes.includes(statusCodeOf(row)))
const notStatus = (collection: string, codes: string[]) =>
  readTable(collection).filter(row => !codes.includes(statusCodeOf(row)))
const discOf = (code: string, wh: 'RM' | 'FG') =>
  byStatus('discrepancy', [code]).filter(disc => whOf(disc.warehouse_id) === wh && !disc.parent_id)
const sessionsOf = (wh: 'RM' | 'FG') => readTable('opname_session').filter(row => whOf(row.warehouse_id) === wh)
const mine = (collection: string, me: string, open: (row: LocalRow) => boolean = OPEN_TASK) =>
  readTable(collection).filter(row => idOf(row.operator_id) === me && open(row))

/** Prototype `C.approvalTertua`: dokumen Pending tertua (PR, RO, Transfer, Selisih) untuk foot "Notifikasi saya". */
function oldestPending() {
  const pending = [
    ...byStatus('purchase_request', ['PRC_PENDING_SPV', 'PRC_PENDING_PURCH']),
    ...byStatus('return_order', ['RTN_PENDING_SPV', 'RTN_PENDING_PURCH', 'RTN_PENDING_KADIV']),
    ...byStatus('transfer', ['TRF_PENDING_SPV', 'TRF_PENDING_KADIV']),
    ...byStatus('discrepancy', ['DSR_PENDING_APP'])
  ].map(row => hoursSince(row.date_updated ?? row.date_created))
  return pending.length ? Math.max(...pending) : null
}

/** Slot pallet FG aktif & terpakai (prototype `C.okupansiFg`). */
function fgSlots() {
  const pallets = stockFg({})
    .zones.flatMap(zone => zone.pallets)
    .filter(pallet => pallet.is_active || pallet.units.length > 0)
  return { total: pallets.length, used: pallets.filter(pallet => pallet.units.length > 0).length }
}

function buildCard(key: CommandCenterCardKey, me: string): Card {
  const sla = slaThresholds()
  switch (key) {
    case 'karantina': {
      const entries = readTable('rm_quarantine_entry').filter(entry => entry.quarantine_status === 'IN_QUARANTINE')
      const late = entries.filter(entry => hoursSince(entry.entered_at) / 24 > sla.quarantine_days).length
      return card(key, 'inbound-rm/qc-scheduler', entries.length, 'Batch karantina belum tuntas QC', 'warn', {
        tone: late ? 'danger' : entries.length ? 'info' : 'neutral',
        chip: late ? `${late} lewat SLA` : 'Antrean QC',
        chip_tone: late ? 'danger' : 'info',
        foot: entries.length ? `Ambang ${sla.quarantine_days} hari` : 'Karantina kosong'
      })
    }
    case 'pr-run':
      // Prototype: PR yang belum Closed/Completed — PR tidak pernah ditutup, jadi seluruh PR terhitung.
      return card(key, 'procurement/daftar-pr', readTable('purchase_request').length, 'PR berjalan')
    case 'ng-open':
      return card(
        key,
        'return-order-rm/daftar-ro',
        readTable('ng_ticket').filter(ticket => ticket.ticket_status === 'OPEN').length,
        'NG Ticket open',
        'warn'
      )
    case 'ro-run':
      return card(
        key,
        'return-order-rm/daftar-ro',
        notStatus('return_order', ['RTN_CLOSED', 'RTN_CANCELLED', 'RTN_REJECTED']).length,
        'Return Order berjalan'
      )
    case 'spk-open':
      return card(
        key,
        'request-production/request-material',
        notStatus('material_request', ['REQ_COMPLETED', 'REQ_CANCELLED']).length,
        'Request SPK terbuka'
      )
    case 'trf-requested':
      return card(
        key,
        'wip-transfer/kirim-antar-gudang',
        byStatus('transfer', ['TRF_REQUESTED']).length,
        'Request transfer menunggu diterima'
      )
    case 'disc-rm':
      return card(
        key,
        'stock-opname/selisih',
        discOf('DSR_INVESTIGATION', 'RM').length,
        'Disc RM Investigation',
        'warn',
        {
          foot: 'Telusuri jejak barang di Detail Selisih & Write-off'
        }
      )
    case 'ng-move':
      return card(
        key,
        'inventory-reports/stok-inventory?tab=ng',
        readTable('rm_ng_item').filter(item => item.item_status === 'AWAITING_MOVE').length,
        'Barang NG menunggu dipindah ke Zona NG',
        'warn'
      )
    case 'bap-ready':
      return card(
        key,
        'destruction-rm/berita-acara-pemusnahan',
        byStatus('rm_destruction', ['DSP_RM_APPROVED', 'DSP_RM_ASSIGNED']).length,
        'Berita acara pemusnahan siap dikerjakan'
      )
    case 'notif': {
      const oldest = oldestPending()
      return card(
        key,
        'general/notifications',
        readTable('notification').filter(row => idOf(row.user_id) === me).length,
        'Notifikasi saya',
        'run',
        {
          tone: 'neutral',
          chip: 'Notifikasi',
          chip_tone: 'neutral',
          foot: oldest == null ? 'Tidak ada antrean approval' : `Dokumen tertua tertahan ${ageText(oldest)}`
        }
      )
    }
    case 'my-qc':
      return card(
        key,
        'inbound-rm/qc-rm-operator',
        mine('rm_qc_assignment', me, row => ['QC_ASSIGNED', 'QC_IN_PROGRESS'].includes(statusCodeOf(row))).length,
        'Tugas QC saya'
      )
    case 'my-putaway':
      return card(key, 'inbound-rm/putaway-rm-operator', mine('rm_putaway_task', me).length, 'Tugas putaway saya')
    case 'my-picking':
      return card(key, 'request-production/picking-rm-operator', mine('picking_task', me).length, 'Tugas picking saya')
    case 'my-opname':
      return card(
        key,
        'stock-opname/opname-operator',
        mine('opname_task', me, row => row.task_status !== 'COMPLETED').length,
        'Tugas opname saya'
      )
    case 'opn-validation-rm':
    case 'opn-validation-fg': {
      const wh = key === 'opn-validation-rm' ? 'RM' : 'FG'
      return card(
        key,
        `stock-opname/daftar-sesi${wh === 'FG' ? '?wh=WH-FG' : ''}`,
        sessionsOf(wh).filter(row => statusCodeOf(row) === 'OPN_VALIDATION').length,
        'Hasil hitung opname menunggu pengesahan saya',
        'warn'
      )
    }
    case 'pr-spv':
      return card(
        key,
        'procurement/daftar-pr',
        byStatus('purchase_request', ['PRC_PENDING_SPV']).length,
        'PR menunggu approval saya',
        'warn'
      )
    case 'ro-spv':
      return card(
        key,
        'return-order-rm/daftar-ro',
        byStatus('return_order', ['RTN_PENDING_SPV']).length,
        'RO menunggu approval saya',
        'warn'
      )
    case 'sj-spv':
      return card(
        key,
        'wip-transfer/kirim-antar-gudang',
        byStatus('transfer', ['TRF_PENDING_SPV']).length,
        'Surat Jalan menunggu approval saya',
        'warn'
      )
    case 'karantina-run':
      return card(
        key,
        'inbound-rm/qc-scheduler',
        readTable('rm_quarantine_entry').filter(entry => entry.quarantine_status === 'IN_QUARANTINE').length,
        'Karantina berjalan'
      )
    case 'picking-run':
      return card(
        key,
        'request-production/request-material',
        readTable('picking_task').filter(OPEN_TASK).length,
        'Tugas picking RM berjalan'
      )
    case 'ro-kadiv':
      return card(
        key,
        'return-order-rm/daftar-ro',
        byStatus('return_order', ['RTN_PENDING_KADIV']).length,
        'RO menunggu approval final saya',
        'warn'
      )
    case 'sj-kadiv':
      return card(
        key,
        'wip-transfer/kirim-antar-gudang',
        byStatus('transfer', ['TRF_PENDING_KADIV']).length,
        'Surat Jalan menunggu approval final saya',
        'warn'
      )
    case 'wo-rm':
    case 'wo-fg': {
      const wh = key === 'wo-rm' ? 'RM' : 'FG'
      return card(
        key,
        `stock-opname/selisih${wh === 'FG' ? '?wh=WH-FG' : ''}`,
        discOf('DSR_PENDING_APP', wh).length,
        `Write-off ${wh} menunggu approval saya`,
        'warn'
      )
    }
    case 'rekap-harian':
      return card(key, 'request-production/rekap-pengambilan', 1, 'Rekap harian — Mengetahui', 'run', {
        tone: 'neutral',
        chip: 'Pintasan',
        chip_tone: 'neutral',
        foot: 'Rekap pengambilan barang harian'
      })
    case 'pr-purch':
      return card(
        key,
        'procurement/approval-pr',
        byStatus('purchase_request', ['PRC_PENDING_PURCH', 'PRC_APPROVED']).length,
        'PR di meja Purchasing'
      )
    case 'ro-purch':
      return card(
        key,
        'return-order-rm/daftar-ro',
        byStatus('return_order', ['RTN_PENDING_PURCH']).length,
        'RO menunggu approval saya',
        'warn'
      )
    case 'my-request':
      return card(
        key,
        'request-production/request-material',
        notStatus('material_request', ['REQ_COMPLETED', 'REQ_CANCELLED']).filter(row => idOf(row.user_created) === me)
          .length,
        'Request SPK saya terbuka'
      )
    case 'handover-ready':
      return card(
        key,
        'request-production/serah-terima-produksi',
        readTable('handover').filter(row => row.handover_status === 'AWAITING_RECEIPT').length,
        'Serah terima siap diterima',
        'warn'
      )
    case 'wip-pending':
      return card(
        key,
        'wip-transfer/retur-sisa-wip',
        byStatus('wip_return', ['WIP_PENDING_CHECK']).length,
        'Retur WIP menunggu validasi'
      )
    case 'tobacco-today':
      return card(key, 'request-production/request-material', 1, 'Supply tembakau hari ini', 'run', {
        tone: 'neutral',
        chip: 'Harian',
        chip_tone: 'info',
        foot: 'Realisasi ke mesin dan retur sisanya'
      })
    case 'sgi-requested':
      return card(
        key,
        'wip-transfer/kirim-antar-gudang',
        byStatus('transfer', ['TRF_REQUESTED']).filter(row => idOf(row.user_created) === me).length,
        'Request saya menunggu WH Utama'
      )
    case 'in-transit':
      return card(
        key,
        'wip-transfer/kirim-antar-gudang',
        byStatus('transfer', ['TRF_IN_TRANSIT']).length,
        'Kiriman In Transit',
        'warn'
      )
    case 'sgi-putaway':
      // Tidak ada status Receiving / putaway Sansugi (NB-11); terdekat = Diterima Sebagian.
      return card(
        key,
        'wip-transfer/kirim-antar-gudang',
        byStatus('transfer', ['TRF_PARTIAL']).length,
        'Putaway Sansugi berjalan'
      )
    case 'alm-pend':
    case 'alm-pantau':
    case 'alm-run': {
      const asRole = key === 'alm-pend' ? 'admin_wh_fg' : key === 'alm-pantau' ? 'supervisor_wh_fg' : 'kadiv_wh_fg'
      const found = almCards([asRole]).find(entry => entry.key === key)
      return found as Card
    }
    case 'pack-run': {
      const running = readTable('merge_request').filter(
        req => req.kind === 'PACK' && ['GAB_READY', 'GAB_PICKING'].includes(statusCodeOf(req))
      )
      const left = readTable('fg_pack_stock')
        .filter(row => row.status === 'IN_STOCK')
        .reduce((sum, row) => sum + Number(row.qty_pack ?? 0), 0)
      return card(
        key,
        'merge-consolidation-fg/barang-lepas-gabung?tab=pack',
        running.length,
        'Permintaan Pack berjalan',
        'run',
        {
          tone: left ? 'warning' : running.length ? 'info' : 'neutral',
          chip: left ? 'Perhatian' : running.length ? 'Berjalan' : 'Kosong',
          chip_tone: left ? 'warning' : running.length ? 'info' : 'neutral',
          foot: left ? `${fmt(left)} pack sisa belum diminta ke produksi` : 'Tidak ada pack sisa tanpa permintaan'
        }
      )
    }
    case 'batch-prod': {
      const batches = byStatus('fg_labeler_batch', ['FG_IN_PRODUCTION'])
      const feeds = readTable('fg_labeler_feed_log')
      const stage = (batch: LocalRow) =>
        Math.min(3, Math.max(1, feeds.filter(feed => idOf(feed.batch_id) === batch.id).length))
      return card(
        key,
        'inbound-fg/penerimaan-fg',
        batches.length,
        'Batch dalam produksi — data ALM belum lengkap',
        'run',
        {
          chip: batches.length ? 'Menunggu ALM' : 'Kosong',
          chip_tone: batches.length ? 'warning' : 'neutral',
          foot: batches.length
            ? batches
                .slice(0, 2)
                .map(batch => `${String(batch.batch_no).split('-').slice(2).join('-')} ALM ${stage(batch)}/3`)
                .join(' · ')
            : 'Tidak ada batch berjalan'
        }
      )
    }
    case 'held': {
      const held = byStatus('fg_labeler_batch', ['FG_HELD'])
      return card(key, 'inbound-fg/penerimaan-fg', held.length, 'Batch Held', 'warn', {
        tone: held.length ? 'danger' : 'neutral',
        chip: held.length ? 'Perlu verifikasi' : 'Aman',
        chip_tone: held.length ? 'danger' : 'neutral',
        foot: held.length
          ? `Batch ${held.map(batch => String(batch.batch_no).split('-').slice(0, 2).join('-')).join(' · ')}`
          : 'Tidak ada batch tertahan'
      })
    }
    case 'terima':
    case 'terima-ifg05': {
      const ready = byStatus('fg_labeler_batch', ['FG_READY_HANDOVER'])
      const ages = ready.map(batch => hoursSince(batch.date_created))
      const late = ages.filter(age => age > sla.fg_receive_hours)
      return card(
        key,
        key === 'terima-ifg05' ? 'inbound-fg/terima-cek-fg' : 'inbound-fg/penerimaan-fg',
        ready.length,
        'Batch menunggu Terima & Cek',
        'warn',
        {
          tone: late.length ? 'warning' : 'info',
          chip: late.length ? `${late.length} lewat tenggat` : 'Antrean',
          chip_tone: late.length ? 'warning' : 'info',
          foot: late.length
            ? `Terlama ${ageText(Math.max(...late))} · ambang ${sla.fg_receive_hours} jam`
            : `Dalam ambang ${sla.fg_receive_hours} jam`
        }
      )
    }
    case 'konsolidasi': {
      const saran = suggestions() as { free?: number }[]
      const free = saran.reduce((sum, row) => sum + Number(row.free ?? 0), 0)
      const slots = fgSlots()
      return card(
        key,
        'merge-consolidation-fg/konsolidasi-pallet',
        free,
        'Pallet bisa dibebaskan lewat konsolidasi',
        'run',
        {
          tone: free ? 'info' : 'neutral',
          chip: 'Saran sistem',
          chip_tone: 'info',
          foot: free
            ? `+${Math.round((free / Math.max(1, slots.total)) * 100)}% ruang dari ${saran.length} saran`
            : 'Tidak ada pallet yang perlu dipadatkan'
        }
      )
    }
    case 'putaway-fg': {
      const tasks = readTable('fg_putaway_task').filter(OPEN_TASK)
      const ids = new Set(tasks.map(task => task.id))
      const items = readTable('fg_putaway_task_item').filter(item => ids.has(idOf(item.task_id)))
      const done = items.filter(item => item.item_status === 'DONE').length
      return card(key, 'inbound-fg/putaway-fg-monitor', tasks.length, 'Putaway FG berjalan', 'run', {
        foot: items.length
          ? `Progres ${Math.round((done / items.length) * 100)}% · ${done} dari ${items.length} unit`
          : 'Tidak ada unit berjalan'
      })
    }
    case 'forecast':
    case 'forecast-sales':
      return card(
        key,
        'outbound-fg/order-pengiriman',
        byStatus('delivery_plan', ['OFG_FORECAST']).filter(plan => !plan.is_void).length,
        key === 'forecast' ? 'Picking order Forecast' : 'Planning ter-import (Forecast)',
        'run',
        key === 'forecast' ? { foot: 'Menunggu dirilis dan dibagi ke petugas' } : {}
      )
    case 'staged': {
      const staged = byStatus('delivery_plan', ['OFG_STAGED']).filter(plan => !plan.is_void).length
      return card(key, 'outbound-fg/order-pengiriman', staged, 'Order siap Shipped', 'run', {
        foot: `Area staging ${staged ? 'menunggu keberangkatan' : 'tuntas & bersih'}`
      })
    }
    case 'claim':
    case 'claim-sales': {
      const running = notStatus('claim', ['CLM_COMPLETED']).length
      return card(
        key,
        'customer-claim/daftar-claim',
        running,
        key === 'claim' ? 'Claim berjalan' : 'Claim customer berjalan',
        'warn',
        key === 'claim' ? { foot: running ? 'Ada sengketa retur konsumen' : 'Tidak ada sengketa retur konsumen' } : {}
      )
    }
    case 'disc-fg':
      return card(
        key,
        'stock-opname/selisih?wh=WH-FG',
        discOf('DSR_INVESTIGATION', 'FG').length,
        'Disc FG Investigation',
        'warn',
        { foot: discDeadlineFg() }
      )
    case 'my-fg-putaway':
      return card(key, 'inbound-fg/putaway-fg-operator', mine('fg_putaway_task', me).length, 'Tugas putaway FG saya')
    case 'my-fg-picking':
      return card(key, 'outbound-fg/picking-fg-operator', mine('fg_picking_task', me).length, 'Tugas picking FG saya')
    case 'my-rts':
      return card(key, 'outbound-fg/return-to-stock-fg', mine('fg_rts_task', me).length, 'Tugas return-to-stock saya')
    case 'release-spv':
      return card(
        key,
        'outbound-fg/order-pengiriman',
        byStatus('delivery_plan', ['OFG_PENDING_SPV']).length,
        'Release menunggu persetujuan saya',
        'warn'
      )
    case 'order-released':
      return card(
        key,
        'outbound-fg/order-pengiriman',
        byStatus('delivery_plan', ['OFG_RELEASED']).length,
        'Order FG berjalan'
      )
    case 'label-stock': {
      const slots = fgSlots()
      return card(
        key,
        'inventory-reports/stok-inventory',
        readTable('fg_label').filter(label => label.label_status === 'IN_STOCK').length,
        'Label FG In Stock',
        'ok',
        {
          tone: 'neutral',
          chip: 'Stok',
          chip_tone: 'neutral',
          foot: `${slots.used} dari ${slots.total} slot pallet terpakai`
        }
      )
    }
    case 'opn-fg-report':
      return card(key, 'stock-opname/laporan?wh=WH-FG', sessionsOf('FG').length, 'Sesi opname FG (laporan)', 'run', {
        tone: 'neutral',
        foot: 'Buka laporan rekonsiliasi per sesi'
      })
    case 'inv03':
      return card(key, 'inventory-reports/dashboard-operasional', 1, 'Dashboard operasional', 'run', {
        tone: 'neutral',
        chip: 'Pintasan',
        chip_tone: 'neutral',
        foot: 'Kapasitas, arus barang, dan kinerja'
      })
  }
}

/** Ringkasan stok per cakupan (prototype `globalCards`). */
function globalCards(fg: boolean): Card[] {
  if (fg) {
    const labels = readTable('fg_label')
    const slots = fgSlots()
    return [
      card(
        'g-label-stock',
        'inventory-reports/stok-inventory',
        labels.filter(label => label.label_status === 'IN_STOCK').length,
        'Label FG In Stock',
        'ok',
        {
          tone: 'neutral',
          chip: 'Stok',
          chip_tone: 'neutral',
          foot: `${slots.used} dari ${slots.total} slot pallet terpakai`
        }
      ),
      card(
        'g-label-out',
        'inventory-reports/stok-inventory',
        labels.filter(label => ['PICKED', 'STAGED'].includes(String(label.label_status))).length,
        'Label FG dalam proses keluar',
        'run',
        { tone: 'neutral', chip: 'Proses', chip_tone: 'info', foot: 'Sudah dipick atau menunggu di area staging' }
      ),
      card(
        'g-batch-open',
        'inbound-fg/penerimaan-fg',
        notStatus('fg_labeler_batch', ['FG_PUTAWAY_COMPLETED']).length,
        'Batch FG belum tuntas putaway',
        'run',
        { tone: 'neutral', chip: 'Inbound', chip_tone: 'info', foot: 'Termasuk batch ditahan dan menunggu dicek' }
      ),
      card(
        'g-slot-free',
        'merge-consolidation-fg/konsolidasi-pallet',
        slots.total - slots.used,
        'Slot pallet masih bebas',
        'ok',
        { chip: 'Kapasitas', chip_tone: 'success', foot: `Dari ${slots.total} slot pallet aktif` }
      )
    ]
  }
  const units = readTable('rm_unit')
  const warehouseOf = (unit: LocalRow) => {
    const zone = findRow('warehouse_zone', idOf(unit.warehouse_zone_id))
    return String(findRow('mt_warehouses', idOf(zone?.warehouse_id))?.code ?? 'WH-RM')
  }
  const inStock = units.filter(unit => unit.unit_status === 'IN_STOCK')
  return [
    card(
      'g-rm-main',
      'inventory-reports/stok-inventory',
      inStock.filter(unit => warehouseOf(unit) === 'WH-RM').length,
      'Unit In Stock — WH Utama',
      'ok'
    ),
    card(
      'g-rm-sgi',
      'inventory-reports/stok-inventory',
      inStock.filter(unit => warehouseOf(unit) === 'WH-SGI').length,
      'Unit In Stock — Sansugi',
      'ok'
    ),
    card(
      'g-rm-putaway',
      'inventory-reports/stok-inventory',
      units.filter(unit => unit.unit_status === 'AWAITING_PUTAWAY').length,
      'Unit menunggu putaway',
      'run',
      { tone: 'neutral', chip: 'Antrean', chip_tone: 'info', foot: 'Sudah lolos QC, belum masuk rak' }
    ),
    card(
      'g-rm-ng',
      'inventory-reports/stok-inventory?tab=ng',
      units.filter(unit => ['NG_IN_QUARANTINE', 'NG_AWAITING_PLACEMENT'].includes(String(unit.unit_status))).length,
      'Unit NG (Karantina NG)',
      'warn',
      { tone: 'warning', chip: 'Terkunci', chip_tone: 'warning', foot: 'Hanya keluar lewat Return Order ke supplier' }
    )
  ]
}

/** Tile KPI ringkasan (prototype `kpiRowHtml`); `spark` = barang masuk (putaway) 7 hari terakhir pada tile stok. */
function kpiTiles(fg: boolean, warehouses: string[]) {
  const pendingDocs =
    byStatus('purchase_request', ['PRC_PENDING_SPV', 'PRC_PENDING_PURCH']).length +
    byStatus('return_order', ['RTN_PENDING_SPV', 'RTN_PENDING_PURCH', 'RTN_PENDING_KADIV']).length +
    byStatus('transfer', ['TRF_PENDING_SPV', 'TRF_PENDING_KADIV']).length +
    byStatus('discrepancy', ['DSR_PENDING_APP']).length
  const approval = {
    key: 'k-approval',
    label: 'Dokumen menunggu approval',
    value: pendingDocs,
    sub: 'PR + RO + Surat Jalan + write-off',
    to: 'general/notifications',
    link: 'Notifikasi'
  }
  if (fg)
    return [
      {
        key: 'k-label',
        label: 'Label FG In Stock',
        value: readTable('fg_label').filter(label => label.label_status === 'IN_STOCK').length,
        sub: 'unit berlabel di zone',
        spark: flow14(true).incoming.slice(-7),
        to: 'inventory-reports/stok-inventory',
        link: 'Lihat stok'
      },
      {
        key: 'k-staged',
        label: 'Order siap dikirim',
        value: byStatus('delivery_plan', ['OFG_STAGED']).filter(plan => !plan.is_void).length,
        sub: 'staging tuntas',
        to: 'outbound-fg/order-pengiriman',
        link: 'Order Pengiriman FG'
      },
      approval
    ]
  const rm = stockRm({ kelompok: 'all', scope_whs: warehouses })
  const pesan = rm.materials.filter(row => row.status_stok === 'PESAN').length
  return [
    {
      key: 'k-rm-stock',
      label: 'Unit RM In Stock',
      value: rm.counts.stok,
      sub: warehouses.join(' + '),
      spark: flow14(false).incoming.slice(-7),
      to: 'inventory-reports/stok-inventory',
      link: 'Lihat stok'
    },
    {
      key: 'k-pesan',
      label: 'Material status PESAN',
      value: pesan,
      sub: 'stock-out ≤ lead time',
      chip: pesan ? 'perlu PR' : 'aman',
      chip_tone: (pesan ? 'danger' : 'success') as Tone,
      to: 'procurement/daftar-pr?tab=rop',
      link: 'Papan ROP'
    },
    {
      key: 'k-karantina',
      label: 'Karantina belum tuntas',
      value: readTable('rm_quarantine_entry').filter(entry => entry.quarantine_status === 'IN_QUARANTINE').length,
      sub: 'menunggu QC / penjadwalan',
      to: 'inbound-rm/qc-scheduler',
      link: 'QC Barang RM'
    },
    approval
  ]
}

/** Hari Jakarta (`YYYY-MM-DD`) dari stempel ISO. */
const jakartaDay = (iso: unknown) => {
  const date = new Date(String(iso ?? ''))
  return Number.isNaN(date.getTime()) ? '' : new Date(date.getTime() + 7 * 3600_000).toISOString().slice(0, 10)
}
const firstName = (id: string) => String(findRow('directus_users', id)?.first_name ?? '')
const operatorIds = () =>
  readTable('directus_users')
    .map(user => String(user.id))
    .filter(id => roleCodesOf(id).some(code => code === 'operator_wh_rm' || code === 'operator_wh_fg'))

/** Data grafik blok "Grafik Operasional & Efisiensi" (prototype `chartsHtml`, evidence G1 §B). */
function charts(fg: boolean, warehouses: string[]) {
  const flow = flow14(fg)
  const sum = (values: number[]) => values.reduce((total, value) => total + value, 0)
  const thisWeek = sum(flow.incoming.slice(7))
  const lastWeek = sum(flow.incoming.slice(0, 7))
  const totalOut = sum(flow.outgoing)

  const stock: { label: string; parts: { label: string; value: number }[] }[] = []
  if (fg) {
    const labels = readTable('fg_label')
    stock.push({
      label: 'WH FG (label)',
      parts: [
        { label: 'In Stock', value: labels.filter(label => label.label_status === 'IN_STOCK').length },
        {
          label: 'Picked/Staged',
          value: labels.filter(label => ['PICKED', 'STAGED'].includes(String(label.label_status))).length
        },
        { label: 'Shipped', value: labels.filter(label => label.label_status === 'SHIPPED').length }
      ]
    })
  } else {
    const units = readTable('rm_unit')
    const warehouseOf = (unit: LocalRow) => {
      const zone = findRow('warehouse_zone', idOf(unit.warehouse_zone_id))
      return String(findRow('mt_warehouses', idOf(zone?.warehouse_id))?.code ?? 'WH-RM')
    }
    for (const wh of warehouses) {
      const mine = units.filter(unit => warehouseOf(unit) === wh)
      stock.push({
        label: wh === 'WH-RM' ? 'WH Utama' : 'Sansugi',
        parts: [
          { label: 'In Stock', value: mine.filter(unit => unit.unit_status === 'IN_STOCK').length },
          { label: 'Awaiting Putaway', value: mine.filter(unit => unit.unit_status === 'AWAITING_PUTAWAY').length },
          { label: 'NG / Karantina', value: mine.filter(unit => /^NG_/.test(String(unit.unit_status))).length }
        ]
      })
    }
  }

  const ops = operatorIds()
  const openTask = (row: LocalRow) => OPEN_TASK(row)
  const openRmPutaway = new Set(
    readTable('rm_putaway_task')
      .filter(openTask)
      .map(task => task.id)
  )
  const openFgPutaway = new Set(
    readTable('fg_putaway_task')
      .filter(openTask)
      .map(task => task.id)
  )
  const openPicking = new Set(
    readTable('picking_task')
      .filter(openTask)
      .map(task => task.id)
  )
  const taskOf = (collection: string, id: unknown) => findRow(collection, idOf(id))
  const load = ops
    .map(id => ({
      name: firstName(id),
      qc: readTable('rm_qc_assignment').filter(
        row => idOf(row.operator_id) === id && ['QC_ASSIGNED', 'QC_IN_PROGRESS'].includes(statusCodeOf(row))
      ).length,
      putaway:
        readTable('rm_putaway_task_item').filter(
          item =>
            openRmPutaway.has(idOf(item.task_id)) &&
            idOf(taskOf('rm_putaway_task', item.task_id)?.operator_id) === id &&
            Number(item.qty_placed ?? 0) < Number(item.qty_assigned ?? 0)
        ).length +
        readTable('fg_putaway_task_item').filter(
          item =>
            openFgPutaway.has(idOf(item.task_id)) &&
            idOf(taskOf('fg_putaway_task', item.task_id)?.operator_id) === id &&
            item.item_status !== 'DONE'
        ).length,
      picking: readTable('picking_task_item').filter(
        item =>
          openPicking.has(idOf(item.task_id)) &&
          idOf(taskOf('picking_task', item.task_id)?.operator_id) === id &&
          item.status === 'PENDING'
      ).length
    }))
    .filter(row => row.qc + row.putaway + row.picking > 0)

  const today = jakartaDay(new Date().toISOString())
  const productivity = ops
    .map(id => {
      let open = 0
      let done = 0
      for (const collection of [
        'rm_putaway_task',
        'fg_putaway_task',
        'fg_picking_task',
        'picking_task',
        'fg_rts_task',
        'opname_task'
      ])
        for (const task of readTable(collection).filter(row => idOf(row.operator_id) === id)) {
          if (task.task_status === 'COMPLETED') {
            const at = task.completed_at ?? task.done_at ?? task.date_updated
            if (!at || jakartaDay(at) === today) done += 1
          } else if (task.task_status !== 'CANCELLED') open += 1
        }
      return { name: firstName(id), done, open }
    })
    .filter(row => row.done + row.open > 0)

  const prs = readTable('purchase_request').map(statusCodeOf)
  const quarantine = readTable('rm_quarantine_entry').filter(entry => entry.quarantine_status === 'IN_QUARANTINE')
  const ageOf = (entry: LocalRow) => Math.floor(hoursSince(entry.entered_at) / 24)

  const pallets = stockFg({})
    .zones.flatMap(zone => zone.pallets)
    .filter(pallet => pallet.is_active || pallet.units.length > 0)
  const labelsIn = readTable('fg_label').filter(label => label.label_status === 'IN_STOCK')
  const onPallet = labelsIn.filter(label => label.pallet_id).length

  return {
    flow: {
      ...flow,
      week_delta: lastWeek ? Math.round(((thisWeek - lastWeek) / lastWeek) * 1000) / 10 : null,
      ratio: totalOut ? Math.round((sum(flow.incoming) / totalOut) * 10) / 10 : null
    },
    capacity: fg
      ? {
          percent: pallets.length
            ? Math.round((pallets.filter(pallet => pallet.units.length > 0).length / pallets.length) * 100)
            : 0,
          used: pallets.filter(pallet => pallet.units.length > 0).length,
          total: pallets.length,
          meters: [
            { label: 'Di pallet (rak utama)', value: onPallet, max: labelsIn.length, unit: 'label' },
            { label: 'Di container / buffer', value: labelsIn.length - onPallet, max: labelsIn.length, unit: 'label' },
            {
              label: 'Isi karton ekuivalen',
              value: Math.round(pallets.reduce((total, pallet) => total + pallet.keq, 0)),
              max: pallets.reduce((total, pallet) => total + pallet.capacity_keq, 0),
              unit: 'karton'
            }
          ]
        }
      : null,
    stock,
    cover: fg
      ? []
      : stockRm({ kelompok: 'all', scope_whs: warehouses })
          .materials.filter(row => row.days_of_cover != null)
          .sort((a, b) => (a.days_of_cover ?? 0) - (b.days_of_cover ?? 0))
          .slice(0, 6)
          .map(row => ({
            code: row.code,
            days: row.days_of_cover ?? 0,
            lead: row.lead_days ?? 0,
            status: row.status_stok
          })),
    load,
    productivity,
    pr_status: [
      { label: 'Draft', value: prs.filter(code => code === 'PRC_DRAFT').length },
      { label: 'Menunggu approval', value: prs.filter(code => /PENDING/.test(code)).length },
      { label: 'Approved', value: prs.filter(code => code === 'PRC_APPROVED').length },
      { label: 'Rejected', value: prs.filter(code => /REJECTED/.test(code)).length }
    ],
    quarantine_age: fg
      ? []
      : [
          { label: '0–1 hari', value: quarantine.filter(entry => ageOf(entry) <= 1).length },
          { label: '2–3 hari', value: quarantine.filter(entry => ageOf(entry) >= 2 && ageOf(entry) <= 3).length },
          { label: '≥ 4 hari', value: quarantine.filter(entry => ageOf(entry) >= 4).length }
        ]
  }
}

/** `GET /api/v1/wms/general/command-center` — Dashboard Utama untuk role aktif. */
export function commandCenter(roleCodes: string[] = roleCodesOf(currentUserId()), me: string = currentUserId()) {
  const role = (roleCodes.find(code => code in COMMAND_CENTER_CARDS) ?? 'admin_wh_rm') as CommandCenterRole
  const scope = invScopeOf(roleCodes)
  const roleRow = readTable('mt_roles').find(row => row.code === role)
  const wh = scope.fg ? 'WH-FG' : 'WH-RM'
  const last = readTable('tx_log')
    .filter(row => {
      const code = String(findRow('mt_warehouses', idOf(row.warehouse_id))?.code ?? '')
      return !code || code === wh || (!scope.fg && code === 'WH-SGI')
    })
    .sort((a, b) => String(b.ts).localeCompare(String(a.ts)))[0]
  return {
    role,
    role_label: String(roleRow?.name ?? role),
    scope: scope.fg ? 'FG' : scope.warehouses.length === 1 ? 'SGI' : 'RM',
    scope_label: scope.fg ? 'Finish Goods (WH-FG)' : `Raw Material (${scope.warehouses.join(' + ')})`,
    cards: COMMAND_CENTER_CARDS[role].map(key => buildCard(key, me)),
    show_batches: (COMMAND_CENTER_BATCH_ROLES as readonly string[]).includes(role),
    kpis: kpiTiles(scope.fg, scope.warehouses),
    globals: globalCards(scope.fg),
    charts: charts(scope.fg, scope.warehouses),
    last_activity: last ? { detail: String(last.detail ?? last.action), ts: String(last.ts) } : null
  }
}

export function registerCommandCenterHandlers(mock: MockAdapter) {
  mock.onGet('/api/v1/wms/general/command-center').reply(() => handle(() => commandCenter()))
}
